import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, interval, Subscription, forkJoin, Subject } from 'rxjs';
import { map, catchError, tap, debounceTime, finalize, switchMap } from 'rxjs/operators';

import { WebSocketService, WebSocketStatus } from './websocket.service';
import { AuthService } from './auth.service';
import { backgroundRequest } from './page-progress.service';
import { environment } from '../../../environments/environment';
import { formatDate } from '../../core/utils/date-format';
import { TranslationService } from './i18n/translation.service';
import {
  Notification,
  NotificationAction,
  NotificationCategory,
  NotificationType,
  NotificationState,
  NotificationFilters,
  NotificationCounts,
  NotificationThread,
  NotificationPreferences,
  NotificationQuickFilter,
  NotificationWsMessage,
  NotificationPriority,
  PaginatedResponse,
  PaginationState,
  DEFAULT_NOTIFICATION_COUNTS,
  NOTIFICATION_CATEGORY_CONFIG,
} from '../models/notification.model';

/** The bell category of each server notification type */
const BACKEND_TYPE_TO_CATEGORY: Record<string, NotificationCategory> = {
  system: 'system',
  order_update: 'order',
  document_ready: 'document',
  alert: 'system',
  payment_reminder: 'payment',
  shipping: 'shipping',
  supplier: 'supplier',
  customer: 'customer',
  approval: 'approval',
};

/** An alert row carries its severity in metadata (card 927): a critical alert is urgent, a warning high */
const ALERT_SEVERITY_TO_PRIORITY: Record<string, NotificationPriority> = {
  critical: 'critical',
  warning: 'high',
  info: 'low',
};

/** GET /communications/notifications/counts/ */
interface BackendNotificationCounts {
  total: number;
  unread: number;
  unread_by_type: Record<string, number>;
  unread_by_severity: Record<string, number>;
}

/** Several pushes close together give one reread */
export const LIVE_REFRESH_DEBOUNCE_MS = 300;

// Import mock data (will be created next)

/**
 * Notification Center Service
 *
 * Provides unified notification management including:
 * - Real-time WebSocket updates
 * - HTTP polling fallback
 * - Signal-based state management
 * - Filtering and searching
 * - Notification grouping by order
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationCenterService implements OnDestroy {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private wsService = inject(WebSocketService);
  private translationService = inject(TranslationService);

  // State signals
  notifications = signal<Notification[]>([]);
  counts = signal<NotificationCounts>(DEFAULT_NOTIFICATION_COUNTS);
  isLoading = signal(false);
  connectionStatus = signal<WebSocketStatus>(WebSocketStatus.DISCONNECTED);

  // Pagination state
  pagination = signal<PaginationState>({
    currentPage: 1,
    pageSize: 50,
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false,
  });

  // Filter state
  activeFilters = signal<NotificationFilters>({});
  activeQuickFilter = signal<NotificationQuickFilter>('all');
  activeCategory = signal<NotificationCategory | 'all'>('all');
  searchQuery = signal('');
  groupByOrder = signal(false);

  // Computed signals
  filteredNotifications = computed(() => this.applyFilters(this.notifications()));

  unreadNotifications = computed(() => this.notifications().filter((n) => n.state === 'unread'));

  actionableNotifications = computed(() =>
    this.notifications().filter(
      (n) =>
        n.state === 'unread' &&
        n.actions &&
        n.actions.length > 0 &&
        (n.category === 'approval' || n.priority === 'critical' || n.priority === 'high')
    )
  );

  notificationThreads = computed(() => this.groupNotificationsByOrder());

  hasUnread = computed(() => this.counts().unread > 0);
  /** The unread count as the bell shows it: above 99 it reads "99+" so the badge keeps its size. */
  unreadBadge = computed(() => {
    const unread = this.counts().unread;
    return unread > 99 ? '99+' : String(unread);
  });
  // From the server's counts, so an urgent alert shows whatever page or filter is open
  hasUrgent = computed(() => this.counts().requiresAction > 0);

  // Subscriptions
  private wsSubscription?: Subscription;
  private statusSubscription?: Subscription;
  private pollingSubscription?: Subscription;
  private refreshSubscription?: Subscription;
  // Live rereads (server pushes, polling, writes that landed): each debounced, the newest wins, stopped on
  // disconnect. Counts and list apart, so a badge reread never cancels a list reread.
  private countsRequests = new Subject<void>();
  private listRequests = new Subject<void>();
  // Whose list and counts the service holds: another user signing in starts empty
  private dataOwner: unknown = null;
  // Archive is local only (the API has no archived field): rereads keep these rows archived
  private archivedIds = new Set<string>();
  // Writes the screen already shows: a background reread that left before one of them is stale
  private writes = 0;

  // Settings
  private readonly WS_ENDPOINT = '/notifications/';
  private readonly POLLING_INTERVAL = 30000; // 30 seconds fallback
  private useWebSocket = true;

  ngOnDestroy(): void {
    this.disconnect();
    this.pollingSubscription?.unsubscribe();
  }

  // ==================== Connection Methods ====================

  /**
   * Connect to WebSocket for real-time updates. The signed-in shells (navbar, admin layout) call it once for the
   * session and disconnect on destroy, so the bell stays live on every page and stops at sign-out.
   */
  connect(): void {
    if (this.refreshSubscription) return; // already live
    const owner = this.authService.currentUser()?.id ?? null;
    if (owner === null || owner !== this.dataOwner) {
      // Another user signed in on this tab: nothing of the previous one stays
      this.notifications.set([]);
      this.counts.set(DEFAULT_NOTIFICATION_COUNTS);
      this.archivedIds.clear();
      this.dataOwner = owner;
    }
    this.refreshSubscription = this.countsRequests
      .pipe(
        debounceTime(LIVE_REFRESH_DEBOUNCE_MS),
        switchMap(() => this.refreshCounts().pipe(catchError(() => of(null))))
      )
      .subscribe();
    this.refreshSubscription.add(
      this.listRequests
        .pipe(
          debounceTime(LIVE_REFRESH_DEBOUNCE_MS),
          // Only on the first page without a search: a push never reloads page 2 under the user
          switchMap(() =>
            this.pagination().currentPage === 1 && !this.searchQuery()
              ? this.fetchNotifications(undefined, { background: true })
              : of(null)
          )
        )
        .subscribe()
    );
    this.refreshSubscription.add(this.fetchNotifications().subscribe());
    this.requestRefresh(false); // the badge from the start

    if (!this.useWebSocket) {
      this.startPolling();
      return;
    }

    // Subscribe to WebSocket status
    this.statusSubscription = this.wsService.getStatus().subscribe((status) => {
      this.connectionStatus.set(status);

      // Poll while the socket is down (an error, or a close that left it paused), not once it is back
      if (status === WebSocketStatus.ERROR || status === WebSocketStatus.PAUSED) {
        this.startPolling();
      } else if (status === WebSocketStatus.CONNECTED) {
        this.stopPolling();
      }
    });

    // Subscribe to WebSocket messages
    this.wsSubscription = this.wsService.connect(this.WS_ENDPOINT).subscribe((message) => {
      this.handleWsMessage(message);
    });
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    this.wsSubscription?.unsubscribe();
    this.statusSubscription?.unsubscribe();
    this.pollingSubscription?.unsubscribe();
    this.refreshSubscription?.unsubscribe();
    this.wsSubscription = undefined;
    this.statusSubscription = undefined;
    this.pollingSubscription = undefined;
    this.refreshSubscription = undefined;
    this.wsService.disconnect();
  }

  /** Ask the server again, soon (debounced): always the badge numbers, the list too when list is true */
  private requestRefresh(list: boolean): void {
    this.countsRequests.next();
    if (list) this.listRequests.next();
  }

  /**
   * Reconnect to WebSocket
   */
  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  /**
   * Start HTTP polling as fallback
   */
  private startPolling(): void {
    if (this.pollingSubscription) return;

    this.requestRefresh(true);
    this.pollingSubscription = interval(this.POLLING_INTERVAL).subscribe(() =>
      this.requestRefresh(true)
    );
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
  }

  /** A write the screen already shows: older background rereads may not undo it, and the list is read again once it lands */
  private afterWrite<T>(request: Observable<T>): Observable<T> {
    this.writes++;
    return request.pipe(finalize(() => this.requestRefresh(true)));
  }

  // ==================== Query Methods ====================

  /**
   * Fetch notifications from API with pagination and filters
   *
   * BACKEND INTEGRATION:
   * --------------------
   * API Endpoint: GET /api/communications/notifications/
   *
   * Query Parameters:
   * | Parameter    | Type     | Description                                      |
   * |--------------|----------|--------------------------------------------------|
   * | page         | number   | Page number (1-indexed)                          |
   * | page_size    | number   | Items per page (default: 15, max: 100)           |
   * | search       | string   | Search in title, message, order_number, customer |
   * | category     | string   | Filter by category (order, approval, document...).|
   * | type         | string   | Filter by notification type                      |
   * | priority     | string   | Filter by priority (critical, high, medium, low) |
   * | read         | boolean  | Filter by read status (true/false)               |
   * | ordering     | string   | Sort field (e.g., '-created_at', 'priority')     |
   *
   * Response Format:
   * {
   *   count: number,        // Total items matching filters
   *   next: string | null,  // URL for next page
   *   previous: string | null, // URL for previous page
   *   results: Notification[]
   * }
   */
  fetchNotifications(
    filters?: NotificationFilters,
    { background = false } = {}
  ): Observable<Notification[]> {
    // A background reread (a live push, polling) keeps the list on screen: no page-level spinner
    const loading = (value: boolean) => background || this.isLoading.set(value);
    loading(true);

    // Build query params from current state
    const _currentPagination = this.pagination();
    const params = this.buildQueryParams(filters);
    // A background reread that the user overtook (other page, filter or search) leaves the screen alone
    const query = JSON.stringify(params);
    const writes = this.writes;
    const overtaken = () =>
      background &&
      (this.writes !== writes || JSON.stringify(this.buildQueryParams(filters)) !== query);

    // Use mock data for development

    // Backend returns paginated response: { count, next, previous, results }
    return this.http
      .get<PaginatedResponse<any>>(`${environment.apiBaseUrl}/communications/notifications/`, {
        params,
        // polls and live-event refreshes of the bell: never page data
        context: backgroundRequest(),
      })
      .pipe(
        tap((response) => {
          if (overtaken()) return;
          console.log('📧 Notifications API response:', response.count, 'total items');
          this.updatePaginationState(response.count, response.next, response.previous);
        }),
        map((response) =>
          response.results
            .map((n) => this.mapBackendNotification(n))
            .map((n) =>
              this.archivedIds.has(n.id) ? { ...n, state: 'archived' as NotificationState } : n
            )
        ),
        tap((notifications) => {
          if (overtaken()) return;
          console.log('📧 Notifications loaded:', notifications.length, notifications[0]);
          // The badge numbers come from /counts/ (connect, live rereads, local changes), not from one page
          this.notifications.set(notifications);
          loading(false);
        }),
        catchError((error) => {
          console.error('Failed to fetch notifications:', error);
          loading(false);
          return of([]);
        })
      );
  }

  /**
   * Build query params for API request
   */
  private buildQueryParams(
    filters?: NotificationFilters
  ): Record<string, string | number | boolean> {
    const params: Record<string, string | number | boolean> = {};
    const currentPagination = this.pagination();

    // Pagination
    params['page'] = currentPagination.currentPage;
    params['page_size'] = currentPagination.pageSize;

    // Search query
    const search = this.searchQuery();
    if (search) {
      params['search'] = search;
    }

    // Category filter - map frontend category to backend type
    // Backend types: order_update, document_ready, payment_reminder, alert, system, approval, customer, supplier, shipping
    const category = this.activeCategory();
    if (category !== 'all') {
      // Every server type of the category (System holds system and alert, card 927)
      const types = Object.keys(BACKEND_TYPE_TO_CATEGORY).filter(
        (type) => BACKEND_TYPE_TO_CATEGORY[type] === category
      );
      if (types.length) {
        params[types.length > 1 ? 'type__in' : 'type'] = types.join(',');
      }
    }

    // Quick filter - map to API params
    const quickFilter = this.activeQuickFilter();
    if (quickFilter === 'unread') {
      params['read'] = false;
    } else if (quickFilter === 'action_required') {
      params['read'] = false;
      params['priority'] = 'high,critical';
    }

    // Additional filters from NotificationFilters object
    if (filters) {
      if (filters.types?.length) {
        params['type'] = filters.types.join(',');
      }
      if (filters.priorities?.length) {
        params['priority'] = filters.priorities.join(',');
      }
      if (filters.states?.length) {
        // Map states to read param
        if (filters.states.includes('unread') && !filters.states.includes('read')) {
          params['read'] = false;
        } else if (filters.states.includes('read') && !filters.states.includes('unread')) {
          params['read'] = true;
        }
      }
      if (filters.orderId) {
        params['order_id'] = filters.orderId;
      }
      if (filters.orderNumber) {
        params['order_number'] = filters.orderNumber;
      }
      if (filters.customerId) {
        params['customer_id'] = filters.customerId;
      }
      if (filters.fromDate) {
        params['created_after'] = filters.fromDate;
      }
      if (filters.toDate) {
        params['created_before'] = filters.toDate;
      }
    }

    // Default ordering - newest first, then by priority
    params['ordering'] = '-created_at';

    return params;
  }

  /**
   * Apply filters and pagination to mock data
   */
  private applyMockFiltersAndPagination(
    notifications: Notification[],
    params: Record<string, string | number | boolean>
  ): { items: Notification[]; total: number } {
    let filtered = [...notifications];

    // Apply search filter
    if (params['search']) {
      const search = (params['search'] as string).toLowerCase();
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(search) ||
          n.message.toLowerCase().includes(search) ||
          n.orderNumber?.toLowerCase().includes(search) ||
          n.customerName?.toLowerCase().includes(search) ||
          n.supplierName?.toLowerCase().includes(search)
      );
    }

    // Apply category filter
    if (params['category']) {
      filtered = filtered.filter((n) => n.category === params['category']);
    }

    // Apply read/unread filter
    if (params['read'] !== undefined) {
      const isRead = params['read'] === true || params['read'] === 'true';
      filtered = filtered.filter((n) => (n.state === 'read') === isRead);
    }

    // Apply priority filter for action_required
    if (params['priority'] && typeof params['priority'] === 'string') {
      const priorities = params['priority'].split(',');
      filtered = filtered.filter((n) => priorities.includes(n.priority));
    }

    // Sort by priority then date
    filtered.sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    const total = filtered.length;

    // Apply pagination
    const page = (params['page'] as number) || 1;
    const pageSize = (params['page_size'] as number) || 15;
    const start = (page - 1) * pageSize;
    const items = filtered.slice(start, start + pageSize);

    return { items, total };
  }

  /**
   * Update pagination state from API response
   */
  private updatePaginationState(
    totalItems: number,
    nextUrl?: string | null,
    prevUrl?: string | null
  ): void {
    const currentPagination = this.pagination();
    const totalPages = Math.ceil(totalItems / currentPagination.pageSize);

    this.pagination.set({
      ...currentPagination,
      totalItems,
      totalPages,
      hasNext: nextUrl !== null && nextUrl !== undefined,
      hasPrevious: prevUrl !== null && prevUrl !== undefined,
    });
  }

  // ==================== Pagination Methods ====================

  /**
   * Go to a specific page
   */
  goToPage(page: number): void {
    const current = this.pagination();
    if (page < 1 || page > current.totalPages) return;

    this.pagination.update((p) => ({ ...p, currentPage: page }));
    this.fetchNotifications().subscribe();
  }

  /**
   * Go to next page
   */
  nextPage(): void {
    const current = this.pagination();
    if (current.hasNext) {
      this.goToPage(current.currentPage + 1);
    }
  }

  /**
   * Go to previous page
   */
  previousPage(): void {
    const current = this.pagination();
    if (current.hasPrevious) {
      this.goToPage(current.currentPage - 1);
    }
  }

  /**
   * Change page size
   */
  setPageSize(size: number): void {
    this.pagination.update((p) => ({
      ...p,
      pageSize: size,
      currentPage: 1, // Reset to first page when changing page size
    }));
    this.fetchNotifications().subscribe();
  }

  /**
   * Reset pagination to first page (used when filters change)
   */
  private resetPagination(): void {
    this.pagination.update((p) => ({ ...p, currentPage: 1 }));
  }

  /**
   * Get single notification by ID
   */
  getNotificationById(id: string): Observable<Notification | undefined> {
    // Check local state first
    const local = this.notifications().find((n) => n.id === id);
    if (local) return of(local);

    return this.http.get<Notification>(
      `${environment.apiBaseUrl}/communications/notifications/${id}`
    );
  }

  /**
   * Refresh notification counts
   */
  refreshCounts(): Observable<NotificationCounts> {
    return this.http
      .get<BackendNotificationCounts>(
        `${environment.apiBaseUrl}/communications/notifications/counts/`,
        {
          context: backgroundRequest(),
        }
      )
      .pipe(
        map((counts) => this.toCounts(counts)),
        tap((counts) => this.counts.set(counts))
      );
  }

  private toCounts(counts: BackendNotificationCounts): NotificationCounts {
    const byCategory = { ...DEFAULT_NOTIFICATION_COUNTS.byCategory };
    for (const [type, count] of Object.entries(counts.unread_by_type)) {
      const category = BACKEND_TYPE_TO_CATEGORY[type] ?? 'system';
      byCategory[category] = (byCategory[category] ?? 0) + count;
    }
    const unreadAt = (priority: NotificationPriority) =>
      Object.entries(counts.unread_by_severity)
        .filter(([severity]) => ALERT_SEVERITY_TO_PRIORITY[severity] === priority)
        .reduce((sum, [, count]) => sum + count, 0);
    const critical = unreadAt('critical');
    const high = unreadAt('high');
    return {
      total: counts.total,
      unread: counts.unread,
      critical,
      high,
      requiresAction: critical + high,
      byCategory,
    };
  }

  // ==================== Action Methods ====================

  /**
   * Mark a single notification as read
   */
  markAsRead(id: string): Observable<void> {
    this.updateNotificationState(id, 'read');
    return this.afterWrite(this.postMarkRead(id));
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<void> {
    // Update local state
    this.notifications.update((notifications) =>
      notifications.map((n) => ({
        ...n,
        state: 'read' as NotificationState,
        readAt: new Date().toISOString(),
      }))
    );
    return this.afterWrite(
      this.http.post<void>(
        `${environment.apiBaseUrl}/communications/notifications/mark_all_read/`,
        {}
      )
    );
  }

  /**
   * Mark multiple notifications as read
   */
  markBatchAsRead(ids: string[]): Observable<void> {
    // Update local state
    this.notifications.update((notifications) =>
      notifications.map((n) =>
        ids.includes(n.id)
          ? { ...n, state: 'read' as NotificationState, readAt: new Date().toISOString() }
          : n
      )
    );
    // The API has no batch endpoint: one mark_read per id
    if (!ids.length) return of(undefined);
    return this.afterWrite(
      forkJoin(ids.map((id) => this.postMarkRead(id))).pipe(map(() => undefined))
    );
  }

  /**
   * Archive a notification (local only: the API has no archived field)
   */
  archive(id: string): Observable<void> {
    // An unread row is marked read too: the badge counts the server's unread rows
    const unread = this.notifications().find((n) => n.id === id)?.state === 'unread';
    this.archivedIds.add(id);
    this.updateNotificationState(id, 'archived');
    return unread ? this.afterWrite(this.postMarkRead(id)) : of(undefined);
  }

  private postMarkRead(id: string): Observable<void> {
    return this.http.post<void>(
      `${environment.apiBaseUrl}/communications/notifications/${id}/mark_read/`,
      {}
    );
  }

  /**
   * Dismiss a notification
   */
  dismiss(id: string): Observable<void> {
    // Remove from local state
    this.notifications.update((notifications) => notifications.filter((n) => n.id !== id));
    return this.afterWrite(
      this.http.delete<void>(`${environment.apiBaseUrl}/communications/notifications/${id}/`)
    );
  }

  /**
   * Snooze a notification
   */
  snooze(id: string, until: Date): Observable<Notification> {
    // Update local state
    const notification = this.notifications().find((n) => n.id === id);
    if (notification) {
      const updated = { ...notification, snoozedUntil: until.toISOString() };
      this.updateLocalNotification(updated);

      return this.http.post<Notification>(
        `${environment.apiBaseUrl}/communications/notifications/${id}/snooze`,
        { until: until.toISOString() }
      );
    }

    return of({} as Notification);
  }

  /**
   * Resolve a notification
   */
  resolve(id: string, notes: string): Observable<Notification> {
    const notification = this.notifications().find((n) => n.id === id);
    if (notification) {
      const updated = {
        ...notification,
        isResolved: true,
        resolvedAt: new Date().toISOString(),
        resolvedNotes: notes,
      };
      this.updateLocalNotification(updated);

      return this.http.post<Notification>(
        `${environment.apiBaseUrl}/communications/notifications/${id}/resolve`,
        { notes }
      );
    }

    return of({} as Notification);
  }

  /**
   * Un-resolve a notification
   */
  unresolve(id: string): Observable<Notification> {
    const notification = this.notifications().find((n) => n.id === id);
    if (notification) {
      const updated = {
        ...notification,
        isResolved: false,
        resolvedAt: undefined,
        resolvedNotes: undefined,
      };
      this.updateLocalNotification(updated);

      return this.http.post<Notification>(
        `${environment.apiBaseUrl}/communications/notifications/${id}/unresolve`,
        {}
      );
    }

    return of({} as Notification);
  }

  // ==================== Filter Methods ====================

  /**
   * Set the active quick filter and fetch from API
   */
  setQuickFilter(filter: NotificationQuickFilter): void {
    this.activeQuickFilter.set(filter);
    this.groupByOrder.set(filter === 'by_order');
    this.resetPagination();
    this.fetchNotifications().subscribe();
  }

  /**
   * Set the active category filter and fetch from API
   */
  setCategoryFilter(category: NotificationCategory | 'all'): void {
    this.activeCategory.set(category);
    this.resetPagination();
    this.fetchNotifications().subscribe();
  }

  /**
   * Set search query and fetch from API
   */
  setSearchQuery(query: string): void {
    this.searchQuery.set(query);
    this.resetPagination();
    this.fetchNotifications().subscribe();
  }

  /**
   * Set full filters object and fetch from API
   */
  setFilters(filters: NotificationFilters): void {
    this.activeFilters.set(filters);
    this.resetPagination();
    this.fetchNotifications().subscribe();
  }

  /**
   * Clear all filters and refetch
   */
  clearFilters(): void {
    this.activeFilters.set({});
    this.activeQuickFilter.set('all');
    this.activeCategory.set('all');
    this.searchQuery.set('');
    this.groupByOrder.set(false);
    this.resetPagination();
    this.fetchNotifications().subscribe();
  }

  /**
   * Apply filters to notifications
   */
  private applyFilters(notifications: Notification[]): Notification[] {
    let filtered = [...notifications];

    // Quick filter
    const quickFilter = this.activeQuickFilter();
    switch (quickFilter) {
      case 'unread':
        filtered = filtered.filter((n) => n.state === 'unread');
        break;
      case 'action_required':
        filtered = filtered.filter(
          (n) =>
            n.state === 'unread' &&
            (n.category === 'approval' || n.priority === 'critical' || n.priority === 'high')
        );
        break;
    }

    // Category filter
    const category = this.activeCategory();
    if (category !== 'all') {
      filtered = filtered.filter((n) => n.category === category);
    }

    // Search query
    const query = this.searchQuery().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(query) ||
          n.message.toLowerCase().includes(query) ||
          n.orderNumber?.toLowerCase().includes(query) ||
          n.customerName?.toLowerCase().includes(query) ||
          n.supplierName?.toLowerCase().includes(query)
      );
    }

    // Advanced filters
    const filters = this.activeFilters();

    if (filters.categories?.length) {
      filtered = filtered.filter((n) => filters.categories!.includes(n.category));
    }

    if (filters.types?.length) {
      filtered = filtered.filter((n) => filters.types!.includes(n.type));
    }

    if (filters.priorities?.length) {
      filtered = filtered.filter((n) => filters.priorities!.includes(n.priority));
    }

    if (filters.states?.length) {
      filtered = filtered.filter((n) => filters.states!.includes(n.state));
    }

    if (filters.orderId) {
      filtered = filtered.filter((n) => n.orderId === filters.orderId);
    }

    if (filters.customerId) {
      filtered = filtered.filter((n) => n.customerId === filters.customerId);
    }

    if (filters.supplierId) {
      filtered = filtered.filter((n) => n.supplierId === filters.supplierId);
    }

    // Filter out snoozed
    filtered = filtered.filter((n) => {
      if (!n.snoozedUntil) return true;
      return new Date(n.snoozedUntil) <= new Date();
    });

    // Sort by date (newest first) and priority
    filtered.sort((a, b) => {
      // Critical/high priority first
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;

      // Then by date
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

    return filtered;
  }

  // ==================== Grouping Methods ====================

  /**
   * Group notifications by order number
   */
  private groupNotificationsByOrder(): NotificationThread[] {
    const notifications = this.filteredNotifications();
    const groups = new Map<string, Notification[]>();

    // Group by order
    notifications.forEach((n) => {
      const key = n.orderNumber || 'no-order';
      const group = groups.get(key) || [];
      group.push(n);
      groups.set(key, group);
    });

    // Convert to threads
    const threads: NotificationThread[] = [];
    groups.forEach((notifs, orderNumber) => {
      if (orderNumber === 'no-order') {
        // Create individual "threads" for non-order notifications
        notifs.forEach((n) => {
          threads.push({
            id: `thread-${n.id}`,
            orderId: '',
            orderNumber: '',
            customerName: n.customerName,
            count: 1,
            unreadCount: n.state === 'unread' ? 1 : 0,
            urgentCount: ['critical', 'high'].includes(n.priority) ? 1 : 0,
            latestAt: n.createdAt,
            notifications: [n],
            isExpanded: true,
          });
        });
      } else {
        const orderId = notifs[0].orderId || '';
        const customerName = notifs[0].customerName;
        const unreadCount = notifs.filter((n) => n.state === 'unread').length;
        const urgentCount = notifs.filter((n) => ['critical', 'high'].includes(n.priority)).length;
        const latestAt = notifs.reduce(
          (latest, n) => (new Date(n.createdAt) > new Date(latest) ? n.createdAt : latest),
          notifs[0].createdAt
        );

        threads.push({
          id: `thread-${orderId}`,
          orderId,
          orderNumber,
          customerName,
          count: notifs.length,
          unreadCount,
          urgentCount,
          latestAt,
          notifications: notifs.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          ),
          isExpanded: false,
        });
      }
    });

    // Sort threads by latest notification
    threads.sort((a, b) => new Date(b.latestAt).getTime() - new Date(a.latestAt).getTime());

    return threads;
  }

  /**
   * Toggle thread expansion
   */
  toggleThread(_threadId: string): void {
    // This would be handled in the component state
  }

  // ==================== Preferences Methods ====================

  /**
   * Get user notification preferences
   */
  getPreferences(): Observable<NotificationPreferences> {
    return this.http.get<NotificationPreferences>(
      `${environment.apiBaseUrl}/communications/notifications/preferences`
    );
  }

  /**
   * Update user notification preferences
   */
  updatePreferences(prefs: Partial<NotificationPreferences>): Observable<NotificationPreferences> {
    return this.http.put<NotificationPreferences>(
      `${environment.apiBaseUrl}/communications/notifications/preferences`,
      prefs
    );
  }

  private getDefaultPreferences(): NotificationPreferences {
    return {
      userId: 'current-user',
      categories: {
        order: { enabled: true, emailEnabled: true, pushEnabled: true, soundEnabled: true },
        approval: { enabled: true, emailEnabled: true, pushEnabled: true, soundEnabled: true },
        document: { enabled: true, emailEnabled: true, pushEnabled: false, soundEnabled: true },
        customer: { enabled: true, emailEnabled: false, pushEnabled: false, soundEnabled: false },
        supplier: { enabled: true, emailEnabled: false, pushEnabled: false, soundEnabled: false },
        payment: { enabled: true, emailEnabled: true, pushEnabled: true, soundEnabled: true },
        shipping: { enabled: true, emailEnabled: true, pushEnabled: false, soundEnabled: false },
        system: { enabled: true, emailEnabled: false, pushEnabled: false, soundEnabled: false },
      },
      soundEnabled: true,
      groupByOrder: true,
      autoArchiveDays: 30,
    };
  }

  // ==================== Private Methods ====================

  /**
   * Handle WebSocket message
   */
  private handleWsMessage(message: NotificationWsMessage): void {
    // What the server's notification socket sends (apps/alerts/consumers.py): a row saved to the bell (an alert,
    // card 927) or a live notice. One path for both: read the badge and the first page again.
    if (message.type === 'alert' || message.type === 'notification') {
      this.requestRefresh(true);
    }
  }

  /**
   * Update notification in local state
   */
  private updateLocalNotification(notification: Notification): void {
    this.notifications.update((notifications) =>
      notifications.map((n) => (n.id === notification.id ? notification : n))
    );
  }

  /**
   * Update a notification's state in the local list
   */
  private updateNotificationState(id: string, state: NotificationState): void {
    const notification = this.notifications().find((n) => n.id === id);
    if (notification) {
      this.updateLocalNotification({
        ...notification,
        state,
        readAt: state === 'read' ? new Date().toISOString() : notification.readAt,
        archivedAt: state === 'archived' ? new Date().toISOString() : notification.archivedAt,
      });
    }
  }

  // ==================== Utility Methods ====================

  /**
   * Get category config
   */
  getCategoryConfig(category: NotificationCategory) {
    return NOTIFICATION_CATEGORY_CONFIG[category];
  }

  /**
   * Get relative time string
   */
  getRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays < 7) return this.translationService.relativeTime(date);

    return formatDate(date);
  }

  /**
   * Get icon for notification type
   */
  getIconForType(type: NotificationType): string {
    const iconMap: Partial<Record<NotificationType, string>> = {
      // Order
      order_received: 'fa-inbox',
      order_status_changed: 'fa-exchange-alt',
      order_blocked: 'fa-ban',
      order_delayed: 'fa-clock',

      // Approval
      approval_proforma: 'fa-file-invoice',
      approval_margin: 'fa-percentage',
      approval_po: 'fa-file-contract',

      // Document
      document_missing: 'fa-file-excel',
      document_expiring: 'fa-calendar-times',
      document_verification_issue: 'fa-exclamation-triangle',
      document_verified: 'fa-check-circle',

      // Customer
      customer_new: 'fa-user-plus',
      customer_kyc_completed: 'fa-user-check',
      customer_credit_approved: 'fa-credit-card',
      customer_credit_denied: 'fa-credit-card',

      // Supplier
      supplier_quote_received: 'fa-file-invoice-dollar',
      supplier_qte_opened: 'fa-envelope-open',
      supplier_po_confirmed: 'fa-handshake',

      // Payment
      payment_received: 'fa-dollar-sign',
      payment_overdue: 'fa-exclamation-circle',

      // Shipping
      booking_confirmed: 'fa-anchor',
      vessel_departed: 'fa-ship',
      documents_sent_dhl: 'fa-paper-plane',
      delivery_confirmed: 'fa-check-double',

      // System
      ai_extraction_complete: 'fa-robot',
      system_error: 'fa-exclamation-triangle',
    };

    return iconMap[type] || 'fa-bell';
  }

  /**
   * Map backend notification format to frontend format
   * Backend sends: type (system, order_update, document_ready, alert, payment_reminder)
   * Frontend expects: category + type
   */
  private mapBackendNotification(backendNotif: any): Notification {
    // Create actions array from action_url and action_label if provided
    const actions: NotificationAction[] = [];
    if (backendNotif.action_url && backendNotif.action_label) {
      // Replace order_number with item_id in the URL for order routes
      let actionRoute = backendNotif.action_url;
      if (backendNotif.order_number && backendNotif.item_id) {
        // Replace /orders/{order_number} with /orders/{item_id}
        actionRoute = actionRoute.replace(
          `/orders/${backendNotif.order_number}`,
          `/orders/${backendNotif.item_id}`
        );
      }

      actions.push({
        id: `action-${backendNotif.id || backendNotif.notification_id}`,
        type: 'navigate',
        label: backendNotif.action_label,
        route: actionRoute,
        icon: this.getActionIcon(backendNotif.type, backendNotif.action_label),
        isPrimary: true,
      });
    }

    return {
      id: backendNotif.id?.toString() || backendNotif.notification_id,
      category: BACKEND_TYPE_TO_CATEGORY[backendNotif.type] || 'system',
      type: backendNotif.type as NotificationType,
      priority:
        backendNotif.priority ||
        ALERT_SEVERITY_TO_PRIORITY[backendNotif.metadata?.severity] ||
        'medium',
      title: backendNotif.title,
      message: backendNotif.message || '',
      state: backendNotif.read ? 'read' : 'unread',
      timestamp: backendNotif.created_at,
      createdAt: backendNotif.created_at,
      source: 'system',
      orderId: backendNotif.item_id?.toString() || backendNotif.order_id?.toString(),
      orderNumber: backendNotif.order_number,
      customerId: backendNotif.metadata?.customer_id?.toString(),
      supplierId: backendNotif.metadata?.supplier_id?.toString(),
      metadata: backendNotif.metadata,
      actions: actions.length > 0 ? actions : undefined,
    } as Notification;
  }

  /**
   * Get appropriate icon for action based on notification type and action label
   */
  private getActionIcon(type: string, label: string): string {
    const labelLower = label.toLowerCase();

    // Based on action label
    if (labelLower.includes('view')) return 'fa-eye';
    if (labelLower.includes('onboard')) return 'fa-user-plus';
    if (labelLower.includes('renew')) return 'fa-sync';
    if (labelLower.includes('review')) return 'fa-clipboard-check';
    if (labelLower.includes('approve')) return 'fa-check';
    if (labelLower.includes('track')) return 'fa-map-marker-alt';
    if (labelLower.includes('contact')) return 'fa-phone';
    if (labelLower.includes('upload')) return 'fa-upload';
    if (labelLower.includes('pay')) return 'fa-dollar-sign';

    // Based on type
    switch (type) {
      case 'customer':
        return 'fa-user';
      case 'supplier':
        return 'fa-industry';
      case 'order_update':
        return 'fa-shopping-cart';
      case 'approval':
        return 'fa-clipboard-check';
      case 'shipping':
        return 'fa-ship';
      case 'payment_reminder':
        return 'fa-dollar-sign';
      case 'document_ready':
        return 'fa-file-alt';
      default:
        return 'fa-arrow-right';
    }
  }
}
